import React from 'react';

interface ToggleProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  label?: string;
  error?: string;
  helperText?: string;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'greenRed';
  className?: string;
}

const Toggle = React.forwardRef<HTMLInputElement, ToggleProps>(
  ({
    checked,
    onChange,
    disabled = false,
    label,
    error,
    helperText,
    size = 'md',
    variant,
    className = '',
    ...props
  }, ref) => {
    const sizeClasses = {
      sm: 'w-8 h-4',
      md: 'w-10 h-5',
      lg: 'w-12 h-6'
    };

    const switchClasses = {
      sm: 'w-3 h-3',
      md: 'w-4 h-4',
      lg: 'w-5 h-5'
    };

    const translateClasses = {
      sm: checked ? 'translate-x-4' : 'translate-x-0.5',
      md: checked ? 'translate-x-5' : 'translate-x-0.5',
      lg: checked ? 'translate-x-6' : 'translate-x-0.5'
    };

    const trackStateClass = variant === 'greenRed'
      ? (checked ? 'bg-green-600 dark:bg-green-500 ring-green-300/40' : 'bg-red-600 dark:bg-red-500 ring-red-300/40')
      : (checked
        ? 'bg-indigo-600 dark:bg-indigo-600 ring-indigo-300/40 dark:ring-indigo-300/50'
        : 'bg-gray-200 dark:bg-slate-700 ring-transparent dark:ring-slate-500/40');

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      onChange(event.target.checked);
    };

    return (
      <div className={`space-y-1 ${className}`}>
       {/* {label && (
          <label className="block text-sm font-medium text-gray-700">
            {label}
          </label>
        )}*/}

        <div className="flex items-center">
          <div className="relative">
            <input
              ref={ref}
              type="checkbox"
              checked={checked}
              onChange={handleChange}
              disabled={disabled}
              className="sr-only"
              {...props}
            />
            <div
              className={`
                ${sizeClasses[size]}
                flex items-center rounded-full shadow-inner ring-1 cursor-pointer transition-colors duration-200
                ${trackStateClass}
                ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                ${error ? 'ring-2 ring-red-300' : ''}
              `}
              onClick={() => !disabled && onChange(!checked)}
            >
              <div
                className={`
                  ${switchClasses[size]}
                  bg-white dark:bg-gray-100 rounded-full shadow transform transition-transform duration-200
                  ${translateClasses[size]}
                  ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}
                `}
              />
            </div>
          </div>

          {label && (
            <span
              className={`ml-3 text-sm ${disabled ? 'text-gray-500 dark:text-gray-400' : 'text-gray-700 dark:text-gray-200'} cursor-pointer`}
              onClick={() => !disabled && onChange(!checked)}
            >
              {label}
            </span>
          )}
        </div>

        {error && (
          <p className="text-sm text-red-600">
            {error}
          </p>
        )}
        {helperText && !error && (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

Toggle.displayName = 'Toggle';

export default Toggle;
